/**
 * Teasers ask — table dédiée, pas getServiceBlogGuides()[0] (vitrine prix SEO).
 * Aucun GET /blog/articles au paint (incrémente view_count).
 */

import i18n from '../config/i18n';
import { METIERS_DATA } from '../pages/MetierLanding/metiersData';
import { classifyAskIntent, normalizeAskText, type AskIntent, type ClassifiedAsk } from './classifyAskIntent';

export type AskAnswerContent = ClassifiedAsk & {
  blogSlug: string | null;
  title: string;
  teaser: string;
  disclaimer: string | null;
};

const askT = (key: string): string => i18n.t(`bookingAsk.${key}`, { ns: 'discovery' });

function pickWhenToConsult(
  seoSlug: string | null,
  matchers: string[]
): { title: string; teaser: string } | null {
  if (!seoSlug) return null;
  const items = METIERS_DATA[seoSlug]?.whenToConsult;
  if (!items?.length) return null;
  const hit = items.find(item => {
    const hay = `${item.title} ${item.desc}`.toLowerCase();
    return matchers.some(m => hay.includes(m));
  });
  const item = hit ?? items[0];
  return { title: item.title, teaser: item.desc };
}

export function resolveAskAnswer(phrase: string, classified?: ClassifiedAsk): AskAnswerContent {
  const result = classified ?? classifyAskIntent(phrase);
  const normalized = normalizeAskText(phrase);

  if (result.intent === 'sante') {
    return {
      ...result,
      blogSlug: 'urgence-veterinaire-pendant-garde',
      title: askT('santeTitle'),
      teaser: askT('santeTeaser'),
      disclaimer: askT('healthDisclaimer'),
    };
  }

  if (result.intent === 'pension') {
    const when = pickWhenToConsult('pension-canine', ['vacances', 'nuit']);
    return {
      ...result,
      blogSlug: 'pension-vs-garde-domicile-chien',
      title: when?.title ?? askT('pensionTitle'),
      teaser: when?.teaser ?? askT('pensionTeaser'),
      disclaimer: null,
    };
  }

  if (result.intent === 'garde') {
    const detruit = normalized.includes('detruit') || normalized.includes('destruction');
    const confiance = normalized.includes('confiance') || normalized.includes('cles');
    const when = pickWhenToConsult(
      'pet-sitter',
      detruit ? ['anxieux', 'seul'] : ['vacances', 'déplacement', 'deplacement']
    );
    let blogSlug = 'vacances-ete-qui-garde-mon-chien';
    let title = when?.title ?? askT('gardeTitle');
    let teaser = when?.teaser ?? askT('gardeTeaser');
    if (detruit) {
      blogSlug = 'chien-detruit-maison-absence-garde';
      title = askT('gardeDestroyTitle');
      teaser = askT('gardeDestroyTeaser');
    } else if (confiance) {
      blogSlug = 'choisir-pet-sitter-criteres';
      title = askT('gardeTrustTitle');
      teaser = askT('gardeTrustTeaser');
    }
    return { ...result, blogSlug, title, teaser, disclaimer: null };
  }

  if (result.intent === 'educ') {
    const rappel = normalized.includes('rappel');
    const mordille = normalized.includes('mordille');
    const tire = normalized.includes('tire') || normalized.includes('laisse');
    const when = pickWhenToConsult(
      'educateur-canin',
      rappel ? ['rappel'] : tire ? ['laisse', 'tire'] : mordille ? ['chiot'] : ['laisse']
    );
    let blogSlug: string | null = 'premiere-seance-education-canine-deroulement';
    if (rappel) blogSlug = 'rappel-chien-ne-revient-pas-solutions';
    else if (mordille) blogSlug = 'chiot-mordille-mains-que-faire';
    else if (tire) blogSlug = null;
    return {
      ...result,
      blogSlug,
      title: when?.title ?? askT('educTitle'),
      teaser: when?.teaser ?? askT('educTeaser'),
      disclaimer: null,
    };
  }

  if (result.intent === 'comportement') {
    const artifice = normalized.includes('artifice') || normalized.includes('feu');
    const detruit = normalized.includes('detruit');
    const when = pickWhenToConsult('comportementaliste-canin', ['peur', 'anxi']);
    let blogSlug = 'chien-peur-autres-chiens-socialisation';
    if (artifice) blogSlug = 'feu-artifice-chien-peur-solutions';
    else if (detruit) blogSlug = 'chien-detruit-maison-absence-garde';
    return {
      ...result,
      blogSlug,
      title: when?.title ?? askT('comportementTitle'),
      teaser: when?.teaser ?? askT('comportementTeaser'),
      disclaimer: null,
    };
  }

  if (result.intent === 'promenade') {
    const canicule = normalized.includes('canicule');
    const when = pickWhenToConsult('promeneur-canin', canicule ? ['météo', 'meteo'] : ['travail']);
    return {
      ...result,
      blogSlug: canicule
        ? 'canicule-promenade-chien-horaires'
        : 'choisir-promeneur-canin-criteres',
      title: when?.title ?? askT('promenadeTitle'),
      teaser: when?.teaser ?? askT('promenadeTeaser'),
      disclaimer: null,
    };
  }

  const when = pickWhenToConsult('educateur-canin', []);
  return {
    ...result,
    blogSlug: null,
    title: when?.title ?? askT('genericTitle'),
    teaser: when?.teaser ?? askT('genericTeaser'),
    disclaimer: null,
  };
}

export function intentFromParam(raw: string | null): AskIntent | null {
  if (
    raw === 'sante' ||
    raw === 'pension' ||
    raw === 'garde' ||
    raw === 'educ' ||
    raw === 'comportement' ||
    raw === 'promenade' ||
    raw === 'generic'
  ) {
    return raw;
  }
  return null;
}
